import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  InputGroup,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { Stack } from '@chakra-ui/layout';
import { useForm } from 'react-hook-form';
import { FiCheck, FiPlus, FiTrash } from 'react-icons/all';
import { FiChevronLeft, FiEdit2, FiX } from 'react-icons/fi';
import { Heading, Input, Table } from '~/components';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { SelectItem } from '~/types';
import { useSinistersInsurers } from '~/hooks/useSinisters/useSinisters';
import apiSinister from '~/utils/apiSinister';
import { SinistersInsurers } from '~/types/sinisters';

type FormData = {
  nm_tipo: string;
};

export const SinistersNewInsurerForm: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    formState,
    clearErrors,
    control,
  } = useForm();
  const { errors } = formState;
  const [isEdit, setEdit] = useState<boolean[]>([]);
  const [nome, setNome] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [nomeEdit, setNomeEdit] = useState<SelectItem[]>([]);
  const [emailEdit, setEmailEdit] = useState<SelectItem[]>([]);
  const [isFetching, setFetching] = useState(false);
  const toast = useToast();
  const {
    data: insurer,
    isLoading: isInsurerLoading,
    refetch,
  } = useSinistersInsurers();

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    clearErrors();
    onClose();
  };

  async function handleCreate(formData: FormData) {
    clearErrors();

    (!nome || !nome?.trim().length) &&
      setError('nome', {
        type: 'required',
        message: 'Informe o nome da seguradora',
      });
    nome.length > 60 &&
      setError('nome', {
        type: 'maxLength',
        message: 'Nome da seguradora com mais de 60 caracteres',
      });

    (!email || !email?.trim().length) &&
      setError('email', {
        type: 'required',
        message: 'Informe o e-mail',
      });
    email.length > 60 &&
      setError('email', {
        type: 'maxLength',
        message: 'E-mail com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = { nome: nome?.trim(), email: email?.trim() };

    setFetching(true);

    apiSinister
      .post(`seguradora/`, form)
      .then(() => {
        toast({
          title: `Seguradora adicionada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setNome('');
        setEmail('');
        setValue('nome', '');
        setValue('email', '');
        queryClient.invalidateQueries('sinistersInsurers');
        queryClient.invalidateQueries('sinistersInsurersAsSelect');
      })
      .catch(error => {
        if (error.response.status === 400) {
          const errorsValidate = Object.entries(error.response.data);
          errorsValidate.forEach(err => {
            toast({
              title: `Erro ao criar seguradora`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao adicionar seguradora`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  async function handleSubmitEdit(index: number) {
    clearErrors();

    (!nomeEdit[index] || !nomeEdit[index].label.trim().length) &&
      setError(`nome-${index}`, {
        type: 'required',
        message: 'Informe o nome da seguradora',
      });

    nomeEdit[index].label.length > 60 &&
      setError(`nome-${index}`, {
        type: 'maxLength',
        message: 'Nome da seguradora com mais de 60 caracteres',
      });

    (!emailEdit[index] || !emailEdit[index].label.trim().length) &&
      setError(`email-${index}`, {
        type: 'required',
        message: 'Informe o e-mail',
      });

    emailEdit[index].label.length > 60 &&
      setError(`email-${index}`, {
        type: 'maxLength',
        message: 'E-mail com mais de 60 caracteres',
      });

    const { _formState } = control;
    if (!_formState.isValid) return;

    const form = {
      nome: nomeEdit[index].label.trim(),
      email: emailEdit[index].label.trim(),
    };

    setFetching(true);

    apiSinister
      .put(`seguradora/${nomeEdit[index]?.value}/`, form)
      .then(() => {
        toast({
          title: `Seguradora editada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        setNomeEdit([...nomeEdit]);
        handleEdit(index);
        queryClient.invalidateQueries('sinistersInsurers');
        queryClient.invalidateQueries('sinistersInsurersAsSelect');
      })
      .catch(error => {
        toast({
          title: `Erro ao editar seguradora`,
          description: `Não foi possível editar a seguradora`,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setFetching(false);
      });
  }

  const handleEdit = (i: number, value?: SinistersInsurers) => {
    clearErrors();
    isEdit[i] = !isEdit[i];
    setEdit([...isEdit]);
    if (value) {
      nomeEdit[i] = { value: value.id.toString(), label: value.nome };
      setNomeEdit([...nomeEdit]);
      emailEdit[i] = { value: value.id.toString(), label: value.email };
      setEmailEdit([...emailEdit]);
    }
  };

  const canEdit = (index: number) => {
    return isEdit[index];
  };

  const handleNome = (event: any) => {
    setNome(event.target.value);
    clearErrors('nome');
  };

  const handleEmail = (event: any) => {
    setEmail(event.target.value);
    clearErrors('email');
  };

  const handleNomeEdit = (event: any, index: number, id: number) => {
    nomeEdit[index] = { value: id.toString(), label: event.target.value };
    clearErrors(`nome-${index}`);
  };

  const handleEmailEdit = (event: any, index: number, id: number) => {
    emailEdit[index] = { value: id.toString(), label: event.target.value };
    clearErrors(`email-${index}`);
  };
  async function handleDelete(id: number) {
    apiSinister
      .delete(`seguradora/${id}/`)
      .then(() => {
        toast({
          title: `Seguradora deletada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('sinistersInsurers');
      })
      .catch(error => {
        if (error.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          toast({
            title: `${error.response.data.detail}`,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setFetching(false);
      });
  }

  return (
    <>
      <Box
        as="button"
        ref={btnRef}
        onClick={handleOpen}
        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
        border="1px"
        px="8px"
        borderRadius="4px"
        fontSize="sm"
        bg="gray.900"
        borderColor="gray.900"
        color="#fff"
        marginLeft="10px"
        h="40px"
      >
        <Flex align="center" gridGap="2">
          <Text fontSize={{ lg: 'sm', md: 'sm' }}>Novo</Text>
        </Flex>
      </Box>
      <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="lg">
        <DrawerOverlay />
        <DrawerContent borderTopLeftRadius="md">
          <DrawerCloseButton />
          <DrawerHeader>
            <Icon
              as={FiChevronLeft}
              style={{
                cursor: 'pointer',
                fontSize: '28px',
                marginRight: '10px',
              }}
              onClick={handleClose}
            />
            Seguradoras cadastradas
          </DrawerHeader>
          <DrawerBody>
            <Stack gridGap={6} bg="white" maxW={550} w="100%">
              <Stack gridGap={4}>
                <FormControl isInvalid={errors?.nome}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    Nome da seguradora *
                  </FormLabel>
                  <Input
                    name="nome"
                    type="text"
                    error={errors.nome}
                    fontSize="15px"
                    placeholder="Entre com o novo nome"
                    onChange={handleNome}
                    value={nome}
                  />
                </FormControl>
                <FormControl isInvalid={errors?.email}>
                  <FormLabel htmlFor="writeUpFile" style={{ fontSize: '14px' }}>
                    E-mail *
                  </FormLabel>
                  <Input
                    name="email"
                    type="text"
                    error={errors.email}
                    fontSize="15px"
                    placeholder="Entre com o novo e-mail"
                    onChange={handleEmail}
                    value={email}
                  />
                </FormControl>
                <Button
                  type="submit"
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  onClick={handleSubmit(handleCreate)}
                  width="30%"
                  height={9}
                >
                  Adicionar
                </Button>
              </Stack>
              <Heading>Lista de seguradoras</Heading>
              <Table type="secondary" size="sm">
                <Thead>
                  <Tr>
                    <Th>Seguradora</Th>
                    <Th style={{ textTransform: 'inherit' }}>E-mail</Th>
                    <Th style={{ textAlign: 'right', paddingRight: '45px' }}>
                      Ações
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {insurer &&
                    !isInsurerLoading &&
                    insurer?.map((t: any, index: number) => (
                      <Tr key={t.id}>
                        {!canEdit(index) ? (
                          <>
                            <Td>
                              <Tooltip label={t.nome} placement="top">
                                <Text isTruncated style={{ width: '190px' }}>
                                  {t.nome}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td>
                              <Tooltip label={t.email} placement="top">
                                <Text isTruncated style={{ width: '190px' }}>
                                  {t.email}
                                </Text>
                              </Tooltip>
                            </Td>
                            <Td
                              style={{
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                                minWidth: '120px',
                              }}
                            >
                              <Tooltip label="Editar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Edit type"
                                  icon={<Icon as={FiEdit2} />}
                                  onClick={() => handleEdit(index, t)}
                                  marginRight="5px"
                                />
                              </Tooltip>
                              <Tooltip label="Deletar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Remove type"
                                  icon={<Icon as={FiTrash} />}
                                  onClick={() => handleDelete(t.id)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        ) : (
                          <>
                            <Td verticalAlign="top">
                              <FormControl isInvalid={errors[`nome-${index}`]}>
                                <Input
                                  name="nome"
                                  type="text"
                                  error={errors[`nome-${index}`]}
                                  fontSize="15px"
                                  placeholder="Editar o nome da seguradora"
                                  size="sm"
                                  defaultValue={nomeEdit[index].label}
                                  onChange={e => handleNomeEdit(e, index, t.id)}
                                  width="190px"
                                />
                              </FormControl>
                            </Td>
                            <Td verticalAlign="top">
                              <FormControl isInvalid={errors[`email-${index}`]}>
                                <Input
                                  name="email"
                                  type="text"
                                  error={errors[`email-${index}`]}
                                  fontSize="15px"
                                  placeholder="Editar o email da seguradora"
                                  size="sm"
                                  defaultValue={emailEdit[index].label}
                                  onChange={e =>
                                    handleEmailEdit(e, index, t.id)
                                  }
                                  width="190px"
                                />
                              </FormControl>
                            </Td>
                            <Td
                              style={{ textAlign: 'right' }}
                              verticalAlign="top"
                              minWidth="120px"
                            >
                              <Tooltip label="Confirmar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Confirm"
                                  icon={<Icon as={FiCheck} />}
                                  marginRight="5px"
                                  onClick={() => handleSubmitEdit(index)}
                                />
                              </Tooltip>
                              <Tooltip label="Cancelar" placement="top">
                                <IconButton
                                  w="25px"
                                  fontSize="20px"
                                  background="transparent"
                                  aria-label="Cancel"
                                  icon={<Icon as={FiX} />}
                                  onClick={() => handleEdit(index)}
                                />
                              </Tooltip>
                            </Td>
                          </>
                        )}
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </Stack>
            <Button
              type="submit"
              onClick={handleClose}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px', marginBottom: '20px' }}
            >
              Voltar
            </Button>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
